import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface DataPoint {
  date: Date | string
  value: number
  category?: string
}

interface LineChartProps {
  data: DataPoint[]
  width?: number
  height?: number
  margin?: { top: number; right: number; bottom: number; left: number }
  title?: string
  xLabel?: string
  yLabel?: string
  colors?: string[]
  showGrid?: boolean
  showTooltip?: boolean
  animate?: boolean
  multiLine?: boolean
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  width = 800,
  height = 400,
  margin = { top: 40, right: 100, bottom: 60, left: 70 },
  title,
  xLabel,
  yLabel,
  colors = ['#4A90E2', '#50C878', '#FF6B35', '#7B68EE', '#E74C3C'],
  showGrid = true,
  showTooltip = true,
  animate = true,
  multiLine = false
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const dimensions = { width, height }

  useEffect(() => {
    if (!svgRef.current || !data.length) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const innerWidth = dimensions.width - margin.left - margin.right
    const innerHeight = dimensions.height - margin.top - margin.bottom

    // Parse dates
    const parseDate = (d: Date | string) => {
      if (d instanceof Date) return d
      return new Date(d)
    }

    const processedData = data.map(d => ({
      ...d,
      date: parseDate(d.date)
    }))

    // Group data by category for multi-line
    const categories = multiLine
      ? Array.from(new Set(processedData.map(d => d.category || 'default')))
      : ['default']

    const groupedData = categories.map(cat => ({
      category: cat,
      values: processedData.filter(d => (d.category || 'default') === cat)
    }))

    // Scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(processedData, d => d.date) as [Date, Date])
      .range([0, innerWidth])

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(processedData, d => d.value) as number * 1.1])
      .nice()
      .range([innerHeight, 0])

    const colorScale = d3.scaleOrdinal<string>()
      .domain(categories)
      .range(colors)

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Grid lines
    if (showGrid) {
      g.append('g')
        .attr('class', 'grid')
        .attr('opacity', 0.1)
        .call(d3.axisLeft(yScale)
          .tickSize(-innerWidth)
          .tickFormat(() => '')
        )
    }

    // Line generator
    const line = d3.line<{ date: Date; value: number }>()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX)

    // Area generator
    const area = d3.area<{ date: Date; value: number }>()
      .x(d => xScale(d.date))
      .y0(innerHeight)
      .y1(d => yScale(d.value))
      .curve(d3.curveMonotoneX)

    // Draw lines and areas for each category
    groupedData.forEach((group, i) => {
      const color = colorScale(group.category)

      // Area under line
      const areaPath = g.append('path')
        .datum(group.values)
        .attr('fill', color)
        .attr('fill-opacity', 0.1)
        .attr('d', area)

      // Line
      const linePath = g.append('path')
        .datum(group.values)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2.5)
        .attr('d', line)

      // Animation
      if (animate) {
        const totalLength = linePath.node()?.getTotalLength() || 0
        linePath
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(1500)
          .ease(d3.easeQuadOut)
          .attr('stroke-dashoffset', 0)

        areaPath
          .attr('opacity', 0)
          .transition()
          .delay(1500)
          .duration(500)
          .attr('opacity', 1)
      }

      // Data points
      const dots = g.selectAll(`.dot-${i}`)
        .data(group.values)
        .enter()
        .append('circle')
        .attr('class', `dot-${i}`)
        .attr('cx', d => xScale(d.date))
        .attr('cy', d => yScale(d.value))
        .attr('r', 0)
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)

      if (animate) {
        dots.transition()
          .delay((_, i) => 1500 + i * 50)
          .duration(300)
          .attr('r', 5)
      } else {
        dots.attr('r', 5)
      }

      // Tooltip interaction
      if (showTooltip && tooltipRef.current) {
        const tooltip = d3.select(tooltipRef.current)

        dots
          .on('mouseover', (event, d) => {
            d3.select(event.currentTarget)
              .transition()
              .duration(200)
              .attr('r', 8)

            tooltip
              .style('opacity', 1)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 10}px`)
              .html(`
                <strong>${group.category !== 'default' ? group.category + '<br/>' : ''}</strong>
                Date: ${d3.timeFormat('%b %d, %Y')(d.date)}<br/>
                Value: ${d.value.toLocaleString()}
              `)
          })
          .on('mouseout', (event) => {
            d3.select(event.currentTarget)
              .transition()
              .duration(200)
              .attr('r', 5)

            tooltip.style('opacity', 0)
          })
      }
    })

    // X Axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .ticks(6)
        .tickFormat(d => d3.timeFormat('%b %Y')(d as Date))
      )
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')

    // Y Axis
    g.append('g')
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => d3.format('.2s')(d as number))
      )

    // X Label
    if (xLabel) {
      svg.append('text')
        .attr('x', margin.left + innerWidth / 2)
        .attr('y', dimensions.height - 10)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#6B7280')
        .text(xLabel)
    }

    // Y Label
    if (yLabel) {
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -(margin.top + innerHeight / 2))
        .attr('y', 15)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#6B7280')
        .text(yLabel)
    }

    // Title
    if (title) {
      svg.append('text')
        .attr('x', dimensions.width / 2)
        .attr('y', 25)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', '600')
        .style('fill', '#1F2937')
        .text(title)
    }

    // Legend (for multi-line)
    if (multiLine && categories.length > 1) {
      const legend = svg.append('g')
        .attr('transform', `translate(${margin.left + innerWidth + 10}, ${margin.top})`)

      categories.forEach((cat, i) => {
        const legendItem = legend.append('g')
          .attr('transform', `translate(0, ${i * 25})`)

        legendItem.append('rect')
          .attr('width', 18)
          .attr('height', 18)
          .attr('rx', 3)
          .attr('fill', colorScale(cat))

        legendItem.append('text')
          .attr('x', 25)
          .attr('y', 13)
          .style('font-size', '12px')
          .style('fill', '#4B5563')
          .text(cat)
      })
    }

  }, [data, dimensions, margin, title, xLabel, yLabel, colors, showGrid, showTooltip, animate, multiLine])

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height} />
      {showTooltip && (
        <div
          ref={tooltipRef}
          style={{
            position: 'absolute',
            opacity: 0,
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            pointerEvents: 'none',
            zIndex: 1000,
            transition: 'opacity 0.2s'
          }}
        />
      )}
    </div>
  )
}

export default LineChart
